
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { 
  Kanban, 
  CheckCircle, 
  Clock, 
  Users,
  TrendingUp,
  AlertCircle
} from 'lucide-react';
import { useBoardContext } from '@/contexts/BoardContext';
import { useScreenSize } from '@/hooks/useScreenSize';
import { useMemo } from 'react';

export const DashboardStats = () => {
  const { boards, allCards } = useBoardContext();
  const { isMobile, isTablet } = useScreenSize();

  const stats = useMemo(() => {
    const totalBoards = boards?.length || 0;
    const totalCards = allCards?.length || 0;
    const completedCards = allCards?.filter(card => card.archived).length || 0;
    const overdueCards = allCards?.filter(card => 
      card.end_date && new Date(card.end_date) < new Date() && !card.archived
    ).length || 0;
    const todayCards = allCards?.filter(card => 
      card.end_date && 
      new Date(card.end_date).toDateString() === new Date().toDateString() &&
      !card.archived
    ).length || 0;

    const completionRate = totalCards > 0 ? Math.round((completedCards / totalCards) * 100) : 0;

    return {
      totalBoards,
      totalCards,
      completedCards,
      overdueCards,
      todayCards,
      completionRate
    };
  }, [boards, allCards]);

  const statsCards = [
    {
      title: 'Total Boards',
      value: stats.totalBoards,
      icon: Kanban,
      color: 'text-blue-600',
      bgColor: 'bg-blue-50 dark:bg-blue-950'
    },
    {
      title: 'Completed Tasks',
      value: stats.completedCards,
      icon: CheckCircle,
      color: 'text-green-600',
      bgColor: 'bg-green-50 dark:bg-green-950',
      badge: `${stats.completionRate}%`
    },
    {
      title: 'Due Today',
      value: stats.todayCards,
      icon: Clock,
      color: 'text-orange-600',
      bgColor: 'bg-orange-50 dark:bg-orange-950'
    },
    {
      title: 'Overdue',
      value: stats.overdueCards,
      icon: AlertCircle,
      color: 'text-red-600',
      bgColor: 'bg-red-50 dark:bg-red-950'
    }
  ];

  return (
    <div className={`grid gap-4 ${
      isMobile 
        ? 'grid-cols-2' 
        : isTablet 
          ? 'grid-cols-2 md:grid-cols-4' 
          : 'grid-cols-2 md:grid-cols-4'
    }`}>
      {statsCards.map((stat, index) => {
        const Icon = stat.icon;
        return (
          <Card key={index} className="hover:shadow-md transition-shadow">
            <CardContent className={`${isMobile ? 'p-4' : 'p-6'}`}>
              <div className="flex items-center justify-between">
                <div>
                  <p className={`text-sm font-medium text-muted-foreground ${isMobile ? 'text-xs' : ''}`}>
                    {stat.title}
                  </p>
                  <div className="flex items-center gap-2 mt-1">
                    <p className={`text-2xl font-bold ${isMobile ? 'text-xl' : ''}`}>
                      {stat.value}
                    </p>
                    {stat.badge && (
                      <Badge variant="secondary" className={isMobile ? 'text-xs' : ''}>
                        {stat.badge}
                      </Badge>
                    )}
                  </div>
                </div>
                <div className={`${stat.bgColor} p-3 rounded-full`}>
                  <Icon className={`${stat.color} ${isMobile ? 'h-5 w-5' : 'h-6 w-6'}`} />
                </div>
              </div>
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
};
